'use client';
import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { ArrowLeft, Search } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { HeaderBackground } from './header-background';
import SolomealHeader from './solomeal-header';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import { LocationMismatchPopover } from '@/components/location-mismatch-popover';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { usePathname, useRouter } from '@/i18n/routing';
import MainHeader from '@/layouts/main/main-header';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import {
  useDetectWebInApp,
  useWebInAppHydrated,
} from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuth } from '@/providers/auth-provider';
import { CITIES, useAiFoodOrderStore, useAuthStore } from '@/store';
import { useLocationMismatchStore } from '@/store/use-location-mismatch-store';

export default function StoreHeader() {
  const searchParams = useSearchParams();
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const address = useAiFoodOrderStore.use.address();
  const cityId = useAiFoodOrderStore.use.city_id();
  const cityLabel =
    CITIES.find((c) => c.value === cityId)?.label ?? 'Hồ Chí Minh';
  const router = useRouter();
  const pathName = usePathname();
  const isMobile = useIsMobile();
  const isWebInApp = useDetectWebInApp();
  // Detection resolve sau mount → consumer cần defer render UserButton/BackButton
  // để tránh flash LogIn icon rồi biến mất khi flip sang WebInApp.
  const hydrated = useWebInAppHydrated();

  const addressCookie = getCookie('ahafood_address');
  const { isLoading } = useAuth();

  const { openCitySheet, isNavigating } = useStoreCitySheet();
  const { user } = useAuthStore();
  const setPendingPrefill = useLocationMismatchStore.use.setPendingPrefill();

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'home',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  const handleToggleAmori = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  }, [user?.cunstomerId]);

  const handleSearchClick = useCallback(() => {
    router.push('/danh-sach-cua-hang?focus=1');
  }, [router]);

  useEffect(() => {
    if (
      !addressCookie &&
      !address &&
      !isNavigating &&
      !isLoading &&
      !latParam &&
      !lngParam
    ) {
      openCitySheet();
    }
  }, [
    addressCookie,
    address,
    isNavigating,
    isLoading,
    openCitySheet,
    latParam,
    lngParam,
  ]);

  if (pathName.includes('/danh-sach-cua-hang')) {
    return null;
  }

  const isSolomealPage =
    pathName.includes('/solomeal') && !pathName.includes('/danh-sach-cua-hang');

  if (isSolomealPage) {
    return <SolomealHeader />;
  }

  if (!isMobile) {
    return <MainHeader />;
  }

  const addressLabel = address?.address ?? cityLabel;
  const hasAddress = Boolean(address?.address);

  return (
    <>
      <NotificationDemo />
      <HeaderBackground className="px-4 py-5">
        <header
          className={cn('mx-auto flex flex-col w-full max-w-screen-xl gap-4')}
        >
          {/* Address row — plain text, flex-col 2 dòng.
              WebInApp: back button bên trái, không user icon.
              Non-WebInApp: user icon ngoài cùng phải.
              Trước khi hydrated: skeleton placeholder để tránh flash. */}
          <div className="flex items-center gap-0">
            {hydrated && isWebInApp ? (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => {
                  logBackButtonClick();
                  clearDebugLogs();
                  JSFunction.call({ name: 'close' });
                }}
                className="flex shrink-0 items-center -ml-2 justify-center rounded-full [&_svg]:size-6 hover:bg-transparent"
                aria-label="Đóng"
              >
                <ArrowLeft className="size-6 text-neutral-70" />
              </Button>
            ) : null}
            <LocationMismatchPopover
              side="bottom"
              align="start"
              onChangeAddress={(gps) => {
                setPendingPrefill(gps);
                openCitySheet();
              }}
            >
              <button
                type="button"
                onClick={() => openCitySheet()}
                className="flex flex-col items-start text-left py-1 -my-1 min-w-0 max-w-[75%]"
              >
                <span className="text-base text-neutral-70 font-normal leading-tight flex items-center gap-1">
                  <Image
                    src={`${assetPrefix}/static/icons/location.svg`}
                    alt=""
                    width={18}
                    height={18}
                    className="shrink-0"
                  />{' '}
                  Giao đến
                </span>
                <span className="flex items-center gap-0 w-full min-w-0">
                  <span className="text-base text-neutral-90 font-semibold leading-snug truncate">
                    {hasAddress ? addressLabel : 'Chọn địa chỉ'}
                  </span>
                </span>
              </button>
            </LocationMismatchPopover>
            {!hydrated ? null : !isWebInApp ? (
              <div className="ml-auto shrink-0">
                <UserButton />
              </div>
            ) : null}
          </div>

          {/* Search pill + Amori button, ngang hàng */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSearchClick}
              className="flex items-center gap-3 h-12 flex-1 px-3 rounded-xl border bg-white"
              aria-label="Tìm món, tìm quán"
            >
              <Search className="shrink-0 size-5 text-neutral-60" />
              <span className="text-base font-normal text-neutral-50">
                Bạn đang thèm gì nào?
              </span>
            </button>
            <button
              type="button"
              onClick={handleToggleAmori}
              className="relative size-12 shrink-0 rounded-full overflow-hidden"
              aria-label="Mở Amori — trợ lý món ăn AI"
            >
              <Image
                src={`${assetPrefix}/static/images/amori-nav-avt.webp`}
                alt="Amori"
                fill
                className="object-cover"
              />
            </button>
          </div>
        </header>
      </HeaderBackground>
    </>
  );
}
